import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Button, Box, Card, CardContent, TextField, Typography } from '@mui/material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import smallicon from './smallicon.png';

export function GPTChat({ doughData, openChat, setOpenChat }) {
  const [messages, setMessages] = useState([
    {
      role: 'system',
      content:
        'You are a helpful assistant specializing in making sourdough bread at home. You will receive user questions about sourdough, plus some "doughData" in the background. Use that data to improve your answers where appropriate. Respond in Markdown format.'
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTyping, setShowTyping] = useState(false);

  // Handle Enter key submission
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault(); // Prevents line break in TextField
      handleSend();
    }
  };

  // Append user's message & get GPT-4 response
  const handleSend = async () => {
    if (!userInput.trim()) return;

    const newMessages = [...messages, { role: 'user', content: userInput }];
    setMessages(newMessages);
    setUserInput('');
    setLoading(true);
    setShowTyping(true);

    try {
      const systemMessage = {
        role: 'system',
        content: `
          You are a helpful assistant that specializes in sourdough baking.
          Respond in **Markdown format** for clarity. The user currently has the following dough calculation data:
          - **Flour**: ${doughData.flour} g  
          - **Protein**: ${doughData.protein} %  
          - **Starter**: ${doughData.starter} g  
          - **Starter Hydration**: ${doughData.starterHydration} %  
          - **Dough Hydration**: ${doughData.doughHydration} %  
          - **Temperature**: ${doughData.temperature} ${doughData.isFahrenheit ? '°F' : '°C'}  
          Use this information to tailor responses appropriately.
        `
      };

      // CHANGED HERE: now POST to our local /api/fetch
      const response = await axios.post('/api/fetch', {
        model: 'gpt-4o-mini-2024-07-18',
        messages: [systemMessage, ...newMessages],
        max_output_tokens: 2500,
        temperature: 0.7
      });

      const botReply = response.data.choices[0].message.content;
      setMessages([...newMessages, { role: 'assistant', content: botReply }]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Oops! domigo is cheap and decided it took too much work to respond. They say that is what happens when you work for free 🤷' }
      ]);
    } finally {
      setLoading(false);
      setShowTyping(false);
    }
  };

  return (
    <Dialog
      open={openChat}
      onClose={() => setOpenChat(false)}
      maxWidth="sm"
      fullWidth
      fullScreen={{ xs: true, sm: false }} // Full-screen only on mobile
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: '#f7f0e7',
          borderRadius: { xs: 0, sm: '12px' },
          width: { xs: '100%', sm: '500px' },
          maxHeight: { sm: '80vh' }
        }
      }}
    >
      {/* Close Button for Mobile */}
      <DialogActions
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'absolute',
          top: 25,
          right: -5,
          zIndex: 10
        }}
      >
        <Button
          onClick={() => setOpenChat(false)}
          sx={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            color: '#5c3c14',
            fontSize: '18px',
            fontWeight: 'bold',
          }}
        >
          ✕
        </Button>
      </DialogActions>

      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '100%', sm: 'auto' },
          maxHeight: { sm: '80vh' },
          overflow: 'hidden'
        }}
      >
        {/* "domigo" Title with Bouncing Icon */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={2}
          sx={{ marginTop: 0 }}
        >
          <Typography variant="h4" sx={{ color: '#5c3c14', fontWeight: 'bold', marginRight: -1.8 }}>
            d
          </Typography>
          <img src={smallicon} alt="icon" className="bounceIcon" />
          <Typography variant="h4" sx={{ color: '#5c3c14', fontWeight: 'bold', marginLeft: -1.8 }}>
            migo
          </Typography>
        </Box>

        {/* Chat Window */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            border: '0px solid #c2a080',
            borderRadius: '12px',
            p: 2,
            mb: 2,
            maxHeight: { xs: '70vh', sm: '60vh' },
          }}
        >
          {messages
            .filter((m) => m.role !== 'system')
            .map((message, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end', // Assistant: left, User: right
                  width: '100%',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    paddingLeft: 1.2,
                    paddingRight: 1.2,
                    paddingTop: 1,
                    paddingBottom: 1,
                    lineHeight: 1.2,
                    borderRadius: '12px',
                    backgroundColor: message.role === 'assistant' ? '#e1d5c9' : '#c2a080',
                    color: '#3e2b1a',
                    maxWidth: '75%',
                    alignSelf: 'flex-start',
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      '& *': {
                        margin: 0,
                        padding: 0
                      },
                      '& ul, & ol': {
                        marginLeft: '16px',
                        paddingLeft: '16px',
                      },
                      '& li': {
                        marginBottom: '4px',
                      }
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <span {...props} />,
                        br: () => null,
                        ul: ({ node, ...props }) => <ul {...props} />,
                        ol: ({ node, ...props }) => <ol {...props} />,
                        li: ({ node, ...props }) => <li style={{ marginBottom: '4px', display: 'list-item' }} {...props} />
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </Box>
                </Box>
              </Box>
            ))}

          {/* Typing Animation */}
          {showTyping && (
            <Box sx={{ textAlign: 'left', mt: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  display: 'inline-block',
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#e1d5c9',
                  maxWidth: '75%'
                }}
              >
                <span className="typing-dots">• • •</span>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Input Box & Send Button */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#fdfdf7',
              }
            }}
          />

          <Button
            variant="contained"
            onClick={handleSend}
            sx={{
              backgroundColor: '#5c3c14',
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: '#4a2c0d'
              }
            }}
          >
            {loading ? 'Send' : 'Send'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default GPTChat;
